import { useState, useCallback } from 'react';
import { useNotificationContext } from '../../interactives/Notifications/context/NotificationContext';
import { notificationPresets } from '../../interactives/Notifications/utils/notification-presets';

export interface UseCopyToClipboardOptions {
  /** Custom success notification message */
  successMessage?: string;
  /** Custom error notification message */
  errorMessage?: string;
  /** Reset copied state after this many milliseconds (default: 2000) */
  resetDelay?: number;
}

export interface UseCopyToClipboardReturn {
  /** Whether content was recently copied */
  isCopied: boolean;
  /** Function to copy text to clipboard. Optional label shown as notification subtitle. */
  copy: (text: string, label?: string) => Promise<boolean>;
}

/**
 * Hook to handle clipboard copy operations with notification integration
 * 
 * Consolidates clipboard copy logic used in SecondaryTag and TertiaryTag components.
 * 
 * @example
 * ```tsx
 * const { copy, isCopied } = useCopyToClipboard();
 * 
 * return (
 *   <button onClick={() => copy('Hello World')}>
 *     {isCopied ? 'Copied!' : 'Copy'}
 *   </button>
 * );
 * ```
 */
export function useCopyToClipboard(options: UseCopyToClipboardOptions = {}): UseCopyToClipboardReturn {
  const { resetDelay = 2000 } = options;
  const { notify } = useNotificationContext();
  const [isCopied, setIsCopied] = useState(false);

  const copy = useCallback(async (text: string, label?: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      notify(notificationPresets.copied(label));
      setIsCopied(true);
      
      // Reset copied state after delay
      setTimeout(() => {
        setIsCopied(false);
      }, resetDelay);
      
      return true;
    } catch (error) {
      notify(notificationPresets.copyFailed());
      return false;
    }
  }, [notify, resetDelay]);

  return {
    isCopied,
    copy
  };
}

